// @ts-check
// Panel "Ejecutar diagrama" (TSK-0058, CAP-EDITOR-EXECUTION, DEC-025).
//
// Sexto slot del editor (ver `editor-controller.js`): se monta con el mismo
// mecanismo ya usado por los otros 5 paneles (destruir/recrear en cada
// `reconstruirDependientes()`, con el `programa` central ya actualizado). No
// llama a `onCambioPrograma`: este panel nunca modifica la IR, solo la
// LEE y la ejecuta, así que no necesita ese callback ni ningún cableado
// nuevo en el controlador aparte de un slot más.
//
// ## Los datos se piden SEGÚN SE EJECUTA, no antes (2026-08-06)
//
// Durante mucho tiempo este panel enseñaba un formulario con todas las
// casillas ANTES de ejecutar. Las contaba recorriendo el árbol en preorden y
// sumando los bloques `Entrada` escritos.
//
// Eso no puede funcionar, y el propio comentario que había aquí lo admitía:
// "casos con bucles que necesiten MÁS valores de los que hay nodos `Entrada`
// quedan fuera de este formulario estático". Traducido: **el editor de
// diagramas no sabía ejecutar un bucle que lee dentro**, que es justo el
// primer programa interesante que escribe un alumno. Cuántos datos hacen
// falta depende de cuántas vueltas dé el bucle, y eso no se sabe sin
// ejecutar. Lo encontró el usuario pasando un `while` de Python al diagrama:
// le salieron dos casillas llamadas las dos "numero", y a la segunda vuelta
// se quedaba sin valores.
//
// Ahora se hace como en los otros dos editores (`pseudocode-editor/
// run-console.js` y `python-editor/execution-console.js`), con el mismo
// componente compartido (`components/input-request-modal.js`) y el mismo
// enfoque *replay*:
//
//   1. Ejecutar con los datos que ya haya (al principio, ninguno).
//   2. Si el motor responde `necesitaEntrada`, pedir ESE dato por ventana,
//      diciendo qué variable espera y enseñando lo que el programa ya ha
//      escrito.
//   3. Añadirlo a la lista y volver al paso 1.
//
// El programa se reejecuta entero desde el principio en cada vuelta. Es
// determinista (mismo programa + mismos datos = mismo resultado), así que
// repetir no cambia nada; y `LIMITE_ENTRADAS` corta un bucle infinito de
// lecturas.
//
// ## Lo que esto cuesta, y por qué se acepta
//
// A diferencia de los otros dos editores, el diagrama se ejecuta en el
// SERVIDOR (`POST /diagrams/execute`): cada dato tecleado es una petición
// más. Un bucle de cinco vueltas son seis llamadas. Se acepta a conciencia
// (decisión del usuario, 2026-08-06) porque la alternativa -- reimplementar
// el intérprete en JavaScript -- duplicaría la regla con la que se CORRIGE,
// que es exactamente la clase de duplicación que este proyecto prohíbe.
//
// El recorrido en preorden del árbol NO desaparece: sigue haciendo falta
// para saber qué `tipo` declaró el alumno en cada bloque `Entrada` y poder
// validar el valor tecleado (ver más abajo). Lo que ya no hace es decidir
// CUÁNTOS datos se piden ni EN QUÉ ORDEN: de eso manda el motor, que es el
// único que lo sabe de verdad.
//
// ## Tipado de los valores introducidos (decisión de diseño)
// El motor acepta cualquier `Valor` (entero, decimal, texto, booleano, lista,
// nulo -- ver `ValorEjecucion` en `diagram-execution-client.js`). Hasta
// TSK-0212, un nodo `Entrada` del modelo NUNCA declaraba ningún tipo
// esperado (solo `variable`), así que este panel no podía saber de antemano
// si la variable se usaría como número o como texto. Cada campo sigue
// siendo un `<input type="text">` (mismo patrón nativo accesible que
// `block-content-panel.js`) cuyo valor se interpreta con una heurística
// simple y predecible (`interpretarValorIntroducido`): cadena vacía tras
// recortar espacios se envía como texto vacío; `"true"`/`"false"` como
// booleano; un entero o decimal con formato `-?\d+` / `-?\d+\.\d+` como
// número; cualquier otra cosa se envía tal cual como texto. Se documenta
// junto al propio formulario (`execution-panel-form-hint`) para que el
// alumno entienda qué escribir.
//
// TSK-0212 (DEC-074): `Entrada.tipo` es ahora un campo NUEVO y OPCIONAL
// (`editor-model.js#crearEntrada`, `backend/src/app/domain/ir.py`) que el
// alumno puede declarar desde `block-content-panel.js`. Cuando una variable
// de Entrada SÍ tiene `tipo` declarado, este panel valida el valor
// introducido contra ESE tipo ANTES de enviarlo a ejecutar
// (`validarValorEntradaSegunTipo`, más abajo), mostrando un error claro y
// accesible (`role="alert"` por campo, mismo patrón ya usado por
// `block-content-panel.js#crearCampoTexto`) si no coincide -- en vez de
// dejar que el motor reciba un valor incoherente con lo que el propio
// alumno declaró. Cuando la variable NO tiene `tipo` declarado (el caso de
// SIEMPRE antes de esta tarea, y el de cualquier diagrama ya existente),
// el comportamiento es EXACTAMENTE el mismo de antes: la heurística
// genérica `interpretarValorIntroducido`, sin ninguna validación adicional.
//
// ## Errores de red/timeout (decisión de diseño)
// `diagram-execution-client.js` propaga `ApiError` sin capturarla (mismo
// contrato que `exercise-client.js`). Este panel SÍ la captura (es quien
// decide cómo mostrarla, docs/03-frontend.md §9.1/§8.4): un fallo de
// red/timeout, un 422 (IR inválida) o cualquier otro error HTTP se traduce a
// un mensaje en español, nunca el mensaje técnico crudo, y se anuncia en una
// región `role="alert"` (asertiva, apropiada para un fallo que impide
// completar la acción que el alumno acaba de pedir) -- no solo en consola.
// Un fallo LÓGICO de la propia ejecución (`exito: false` con HTTP 200, p.ej.
// división por cero o límite de pasos agotado) usa la MISMA región de error:
// para el alumno ambos son "no he podido ver el resultado de mi diagrama",
// y WCAG 2.2 SC 4.1.3 no distingue el origen del fallo, solo exige que se
// anuncie.
//
// ## Ejecución efímera (DEC-025): nada se persiste
// Este panel no guarda nada en `localStorage` ni llama a ningún endpoint de
// guardado: cada pulsación de "Ejecutar" es una petición nueva e
// independiente; el resultado solo vive en el estado en memoria de esta
// instancia (se pierde al reconstruir el panel, igual que cualquier otro
// estado no persistido de los demás paneles).
//
// --------------------------------------------------------------------------
// TSK-0079 (rediseño visual, mockup aprobado Artifact 2026-07-18): el botón
// "Ejecutar" (SOLO el botón que revela el formulario/dispara la ejecución
// sin valores de Entrada -- no el botón "Ejecutar con estos valores" del
// formulario, ni "Cancelar", que se quedan donde están, junto al resto del
// panel) se mueve a la barra de herramientas fija del panel del canvas, en
// vez de vivir en la fila inferior de ancho completo de este panel.
//
// Mecanismo (contrato de `dataset`, ver el comentario extenso en
// `canvas-view.js` junto a `barraHerramientas`/`data-canvas-toolbar-run`,
// que documenta también la CONTRADICCIÓN registrada con
// `rutas_prohibidas` de esta ficha sobre `editor-controller.js`, que impide
// cablear esto de forma explícita entre ambos paneles): este módulo NO
// importa `canvas-view.js` (docs/03-frontend.md §4.2, "componentes hermanos
// no se importan entre sí"), localiza el contenedor de destino por el mismo
// atributo `data-canvas-toolbar-run` documentado en los dos archivos.
//
// Grado de acoplamiento y degradación (deliberados, no un descuido): si no
// existe ningún `[data-canvas-toolbar-run]` en el documento (p.ej. los tests
// unitarios/de accesibilidad de ESTE archivo, que montan `crearPanelEjecucion`
// aislado, sin `canvas-view.js`), el botón se queda exactamente donde ya
// vivía antes de esta tarea -- ninguna de las pruebas existentes que
// localizan "Ejecutar" dentro de `vista.elemento` se rompe. Cuando SÍ existe
// (la composición real de `editor-controller.js`, o cualquier test que monte
// ambos paneles juntos), el botón se traslada allí manteniendo la MISMA
// referencia de nodo DOM (nunca se clona ni se recrea: sigue siendo el
// idéntico `<button>` con sus propios listeners ya registrados, así que su
// `aria-label`/nombre accesible/estado `disabled`/`aria-busy` no cambian de
// significado por moverse de contenedor).
//
// Se intenta el traslado dos veces por el mismo motivo documentado en
// `canvas-view.js`: (1) de forma SÍNCRONA, justo tras construir el DOM de
// este panel -- cubre toda RECONSTRUCCIÓN posterior a la primera (en
// `editor-controller.js`, `crearCanvas()` siempre se ejecuta antes que
// `crearEjecucion()`, así que el contenedor de destino ya está en el
// documento en ese momento); y (2) vía `queueMicrotask`, para cubrir el
// MONTAJE inicial (la composición completa se construye entera, incluida
// esta llamada, ANTES de que el código llamador -- `diagram-editor-page.js`
// -- añada el elemento raíz al documento real; el `document.querySelector`
// síncrono de (1) no puede encontrar nada todavía en ese instante concreto,
// pero si el llamador adjunta el árbol de forma síncrona inmediatamente
// después -- como ya hace hoy `montarPaginaEditor()` -- la cola de
// microtareas se vacía DESPUÉS de ese `append`, así que (2) sí lo encuentra).

import { crearIcono } from '../../components/base/iconos.js';
import { recorrerNodos, TIPO_NODO, esNodo, TIPO_PRIMITIVO } from './editor-model.js';
import { serializarPrograma } from './ir-serialization.js';
import { ejecutarDiagrama } from '../../services/diagram-execution-client.js';
import { ApiError } from '../../services/api-errors.js';
import { crearModalDialogo } from '../../components/modal-dialog.js';
import {
  crearModalPeticionDato,
  LIMITE_ENTRADAS,
  MENSAJE_ENTRADA_CANCELADA,
} from '../../components/input-request-modal.js';

/**
 * @typedef {import('./editor-model.js').NodoModelo} NodoModelo
 * @typedef {import('../../services/diagram-execution-client.js').ResultadoEjecucionDiagrama} ResultadoEjecucionDiagrama
 */

let contadorInstancias = 0;

/** Etiqueta legible en minúsculas del tipo declarado de una variable de
 * Entrada (TSK-0212), usada solo para completar la etiqueta accesible del
 * campo (`crearCampoEntrada`, más abajo) -- p.ej. "tipo entero". */
const ETIQUETA_TIPO_PRIMITIVO_ENTRADA = Object.freeze({
  [TIPO_PRIMITIVO.ENTERO]: 'entero',
  [TIPO_PRIMITIVO.DECIMAL]: 'decimal',
  [TIPO_PRIMITIVO.CADENA]: 'texto',
  [TIPO_PRIMITIVO.BOOLEANO]: 'booleano',
});

/**
 * Un elemento de `variablesEntrada`: nombre de la variable leída y, desde
 * TSK-0212, el tipo declarado en el nodo `Entrada` correspondiente (`null`
 * si no se declaró ninguno -- el caso de siempre antes de esa tarea).
 * @typedef {{ nombre: string, tipo: string | null }} VariableEntrada
 */

/**
 * Recoge, en preorden, el nombre de variable (y su `tipo` declarado, si lo
 * hay -- TSK-0212) de cada nodo `Entrada` dentro de `programa.cuerpo` (ver
 * "Orden de las variables de Entrada" en la cabecera del archivo),
 * expandiendo además cada `Llamada` encontrada con las `Entrada` del cuerpo
 * de la `Funcion` correspondiente (TSK-0210, ver
 * `obtenerVariablesEntradaDeNodo`). Devuelve `[]` si `programa` no tiene un
 * `cuerpo` reconocible (defensivo, no lanza: un programa vacío simplemente
 * no pide ningún valor).
 * @param {NodoModelo} programa
 * @returns {VariableEntrada[]}
 */
function obtenerVariablesEntradaEnOrden(programa) {
  const cuerpo = /** @type {Record<string, unknown>} */ (programa ?? {}).cuerpo;
  if (!esNodo(cuerpo)) return [];
  const funciones = Array.isArray(/** @type {Record<string, unknown>} */ (programa ?? {}).funciones)
    ? /** @type {any[]} */ (/** @type {Record<string, unknown>} */ (programa).funciones)
    : [];
  return obtenerVariablesEntradaDeNodo(/** @type {NodoModelo} */ (cuerpo), funciones, new Set());
}

/**
 * Recorre `raiz` en preorden (`recorrerNodos`, sin reimplementarlo)
 * recogiendo el nombre de variable de cada nodo `Entrada`. Al encontrar una
 * `Llamada`, busca la `Funcion` correspondiente en `funciones` (por
 * `nombre_funcion`) y expande AHÍ MISMO, recursivamente y en el mismo
 * preorden, las variables `Entrada` de `funcion.cuerpo` (TSK-0210: por DEC-073,
 * el caso simple y común -- función llamada una única vez de forma
 * incondicional -- pasa a pedir también sus valores de Entrada).
 *
 * Guarda de recursión (`pilaExpansion`): conjunto de nombres de función ya
 * en expansión en la RAMA de recursión actual, no un "ya visto" global. Cada
 * `Llamada` recibe su propia copia de la pila (`new Set(pilaExpansion)`)
 * antes de añadir su propio nombre, así que dos llamadas a la MISMA función
 * en puntos distintos y no anidados del árbol se expanden las DOS, de forma
 * independiente -- lo único que se evita es que una función se expanda a sí
 * misma transitivamente (directa, `A` se llama a sí misma, o indirecta, `A`
 * llama a `B` y `B` llama de nuevo a `A`). Al detectar que la función que
 * tocaría expandir ya está en la pila de expansión actual, esa rama
 * simplemente se detiene ahí (no se añaden más variables por ese punto de
 * llamada): nunca lanza, nunca cuelga: el motor sigue siendo la autoridad
 * final si de verdad faltan valores en tiempo de ejecución (ver "Orden de
 * las variables de Entrada" en la cabecera del archivo).
 * @param {NodoModelo} raiz
 * @param {any[]} funciones
 * @param {Set<string>} pilaExpansion
 * @returns {VariableEntrada[]}
 */
function obtenerVariablesEntradaDeNodo(raiz, funciones, pilaExpansion) {
  /** @type {VariableEntrada[]} */
  const variables = [];
  for (const nodo of recorrerNodos(raiz)) {
    if (nodo.nodo === TIPO_NODO.ENTRADA && typeof (/** @type {any} */ (nodo).variable) === 'string') {
      const tipoDeclarado = /** @type {any} */ (nodo).tipo;
      variables.push({
        nombre: /** @type {any} */ (nodo).variable,
        tipo: typeof tipoDeclarado === 'string' ? tipoDeclarado : null,
      });
      continue;
    }
    if (nodo.nodo === TIPO_NODO.LLAMADA && typeof (/** @type {any} */ (nodo).nombre_funcion) === 'string') {
      const nombreFuncion = /** @type {any} */ (nodo).nombre_funcion;
      if (pilaExpansion.has(nombreFuncion)) continue;
      const funcion = funciones.find((candidata) => candidata && candidata.nombre === nombreFuncion);
      if (!esNodo(funcion?.cuerpo)) continue;
      const pilaExpansionHija = new Set(pilaExpansion);
      pilaExpansionHija.add(nombreFuncion);
      variables.push(...obtenerVariablesEntradaDeNodo(funcion.cuerpo, funciones, pilaExpansionHija));
    }
  }
  return variables;
}

/**
 * Ver "Tipado de los valores introducidos" en la cabecera del archivo.
 * @param {string} crudo
 * @returns {number|string|boolean}
 */
export function interpretarValorIntroducido(crudo) {
  const texto = crudo.trim();
  if (texto === 'true') return true;
  if (texto === 'false') return false;
  if (/^-?\d+$/.test(texto)) return Number.parseInt(texto, 10);
  if (/^-?\d+\.\d+$/.test(texto)) return Number.parseFloat(texto);
  return texto;
}

/**
 * Valida y convierte `crudo` según el `tipo` declarado (TSK-0212, DEC-074) de
 * la variable de Entrada correspondiente, si lo hay. Sin tipo declarado
 * (`tipo` es `null`), delega ÍNTEGRAMENTE en la heurística genérica ya
 * existente `interpretarValorIntroducido`: comportamiento SIN NINGÚN CAMBIO
 * respecto a antes de esta tarea (criterio de retrocompatibilidad).
 *
 * Con tipo declarado, valida FORMATO antes de convertir (nunca delega en la
 * heurística, que podría "adivinar" un tipo distinto al declarado, p.ej.
 * interpretar "true" como booleano cuando el alumno declaró CADENA):
 * - `entero`: solo dígitos (con signo opcional), sin parte decimal.
 * - `decimal`: dígitos con signo opcional y parte decimal opcional (admite
 *   también un entero "puro", igual que aceptaría cualquier decimal real).
 * - `booleano`: únicamente `"true"` o `"false"` (recortando espacios).
 * - `cadena`: cualquier texto es válido tal cual (recortado), sin heurística
 *   de número/booleano -- es la propia declaración del alumno de que ese
 *   valor debe tratarse siempre como texto.
 * @param {string} crudo
 * @param {string | null | undefined} tipo
 * @returns {{ valido: true, valor: number | string | boolean } | { valido: false, mensaje: string }}
 */
export function validarValorEntradaSegunTipo(crudo, tipo) {
  if (!tipo) {
    return { valido: true, valor: interpretarValorIntroducido(crudo) };
  }
  const texto = crudo.trim();
  if (tipo === TIPO_PRIMITIVO.ENTERO) {
    if (!/^-?\d+$/.test(texto)) {
      return { valido: false, mensaje: 'Escribe un número entero, sin decimales (ej. "3" o "-12").' };
    }
    return { valido: true, valor: Number.parseInt(texto, 10) };
  }
  if (tipo === TIPO_PRIMITIVO.DECIMAL) {
    if (!/^-?\d+(\.\d+)?$/.test(texto)) {
      return { valido: false, mensaje: 'Escribe un número decimal (ej. "3.5" o "-0.2").' };
    }
    return { valido: true, valor: Number.parseFloat(texto) };
  }
  if (tipo === TIPO_PRIMITIVO.BOOLEANO) {
    if (texto !== 'true' && texto !== 'false') {
      return { valido: false, mensaje: 'Escribe "true" o "false".' };
    }
    return { valido: true, valor: texto === 'true' };
  }
  if (tipo === TIPO_PRIMITIVO.CADENA) {
    return { valido: true, valor: texto };
  }
  // Tipo desconocido (defensivo -- no debería ocurrir con la UI actual, que
  // solo ofrece `TIPO_PRIMITIVO`): se trata igual que "sin tipo declarado",
  // nunca bloquea al alumno por un valor que este panel no sabe interpretar.
  return { valido: true, valor: interpretarValorIntroducido(crudo) };
}

/**
 * Convierte un valor de ejecución (entero, decimal, texto, booleano, lista,
 * nulo) en un texto legible para mostrarlo en la lista de salidas/variables.
 * @param {unknown} valor
 * @returns {string}
 */
export function formatearValorEjecucion(valor) {
  if (valor === null || valor === undefined) return 'nulo';
  if (typeof valor === 'boolean') return valor ? 'verdadero' : 'falso';
  if (Array.isArray(valor)) return JSON.stringify(valor);
  return String(valor);
}

/**
 * Traduce un error de ejecución (de red/API) a un mensaje en español apto
 * para la interfaz, nunca el mensaje técnico crudo (docs/03-frontend.md
 * §8.4/§9.1).
 * @param {unknown} error
 * @returns {string}
 */
export function mensajeDeErrorEjecucion(error) {
  if (error instanceof ApiError) {
    if (error.kind === 'timeout' || error.kind === 'network_error') {
      return 'No se ha podido conectar con el servidor para ejecutar el diagrama. Comprueba tu conexión e inténtalo de nuevo.';
    }
    if (error.kind === 'validation_error') {
      return 'El diagrama no se puede ejecutar tal como está: revisa que todos los bloques tengan su contenido completo y vuelve a intentarlo.';
    }
    return 'No se ha podido ejecutar el diagrama en el servidor. Inténtalo de nuevo en unos instantes.';
  }
  return 'No se ha podido ejecutar el diagrama. Inténtalo de nuevo.';
}

/**
 * Crea el panel "Ejecutar diagrama".
 *
 * @param {{ root?: HTMLElement, programa?: NodoModelo }} [opciones]
 * @returns {{ elemento: HTMLElement, destruir: () => void }}
 */
export function crearPanelEjecucion(opciones = {}) {
  const programa = opciones.programa;
  const variablesEntrada = esNodo(programa) ? obtenerVariablesEntradaEnOrden(programa) : [];

  contadorInstancias += 1;
  const idInstancia = contadorInstancias;
  const idTitulo = `execution-panel-heading-${idInstancia}`;

  let destruido = false;
  /** @type {AbortController | null} */
  let controladorEnCurso = null;

  // ---- estructura DOM (siempre construida; el formulario se revela o no) ----
  const seccion = document.createElement('section');
  seccion.className = 'execution-panel';
  seccion.setAttribute('aria-labelledby', idTitulo);

  const titulo = document.createElement('h2');
  titulo.id = idTitulo;
  titulo.className = 'execution-panel-heading';
  titulo.textContent = 'Ejecutar diagrama';

  const ayuda = document.createElement('p');
  ayuda.className = 'execution-panel-hint';
  ayuda.textContent =
    variablesEntrada.length > 0
      ? 'Pulsa "Ejecutar": el programa te irá pidiendo cada dato cuando lo necesite.'
      : 'Pulsa "Ejecutar" para ver la salida real de tu diagrama.';

  const botonEjecutar = document.createElement('button');
  botonEjecutar.type = 'button';
  botonEjecutar.className = 'execution-panel-run ui-button ui-button-primario';
  // Rediseño (Fase 4b): la tecla principal con el ▶ de trazo (decorativo).
  botonEjecutar.append(crearIcono('ejecutar'), document.createTextNode('Ejecutar'));

  /** Live region moderada (mismo patrón que el resto de paneles: rol
   * nativo "status", aria-live="polite"/aria-atomic="true" explícitos). */
  const estado = document.createElement('p');
  // Rediseño (Fase 4b): una línea de la consola de las piezas base.
  estado.className = 'execution-panel-status ui-consola ui-consola-linea';
  estado.setAttribute('role', 'status');
  estado.setAttribute('aria-live', 'polite');
  estado.setAttribute('aria-atomic', 'true');
  estado.textContent = '';

  /** Región de error asertiva (ver "Errores de red/timeout" en la cabecera):
   * cubre tanto fallos de red/API como fallos lógicos de la ejecución
   * (`exito: false`). */
  const error = document.createElement('p');
  error.className = 'execution-panel-error';
  error.setAttribute('role', 'alert');
  error.hidden = true;

  // ---- petición de datos: uno cada vez, según los va pidiendo el motor ----
  // Mismo componente y mismo protocolo que las consolas de pseudocódigo y
  // Python (ver la cabecera de este archivo). El diagrama añade una cosa que
  // los otros dos no pueden: el motor dice QUÉ variable espera, así que la
  // ventana lo nombra en vez de pedir "un dato" a secas.
  const modalEntrada = crearModalPeticionDato({
    idCampo: `execution-panel-${idInstancia}-entrada`,
    prefijoClase: 'execution-panel',
    claseCampo: 'execution-panel-input-field',
    obtenerOrigenFoco: () => botonEjecutar,
  });
  const { pedirDato } = modalEntrada;

  /**
   * Tipo declarado de cada variable de Entrada, por nombre. Es lo único que
   * sobrevive del antiguo recorrido estático: sigue haciendo falta para
   * validar lo que el alumno teclea (TSK-0212) contra lo que él mismo
   * declaró en el bloque.
   *
   * Se indexa por NOMBRE y no por posición porque el motor identifica la
   * variable pendiente por su nombre, no por su orden de aparición -- que es
   * precisamente lo que dejó de ser predecible al haber bucles. Si dos
   * bloques `Entrada` leyeran la misma variable con tipos distintos, manda
   * el primero: es un diagrama incoherente de todos modos, y la alternativa
   * (no validar nada) sería peor.
   * @type {Map<string, string>}
   */
  const tipoDeclaradoPorVariable = new Map();
  for (const { nombre, tipo } of variablesEntrada) {
    if (tipo && !tipoDeclaradoPorVariable.has(nombre)) tipoDeclaradoPorVariable.set(nombre, tipo);
  }

  // ---- resultados ----
  const resultados = document.createElement('div');
  resultados.className = 'execution-panel-results';
  resultados.hidden = true;

  // Barra de estado de la última ejecución (mockup aprobado, imagen de
  // referencia, 2026-07-20): resumen de una línea con un indicador de color
  // (punto verde/rojo) + el resultado en texto ("Última ejecución correcta/
  // incorrecta" + variables finales). El TEXTO ya es la señal principal
  // (WCAG 2.2 SC 1.4.1); el punto de color es puramente un refuerzo visual
  // adicional, nunca la única forma de distinguir éxito de fallo -- por eso
  // vive DENTRO de la misma región `estado`/`resultadosTitulo`, no como un
  // segundo indicador aislado sin texto.
  const resumenEjecucion = document.createElement('p');
  resumenEjecucion.className = 'execution-panel-summary';

  const resultadosTitulo = document.createElement('h3');
  resultadosTitulo.className = 'execution-panel-results-heading';
  resultadosTitulo.textContent = 'Salida del programa';

  const resultadosCuerpo = document.createElement('div');
  resultadosCuerpo.className = 'execution-panel-results-body';

  resultados.append(resumenEjecucion, resultadosTitulo, resultadosCuerpo);

  // El resultado de la ejecución (resumen + salida del programa) se muestra
  // ahora en una ventana emergente centrada (petición del usuario), mismo
  // patrón que el resultado de "Corrección"/"Enviar solución". El bloque
  // `resultados` completo vive en el cuerpo del modal; se abre al terminar una
  // ejecución (`mostrarResultado`) y devuelve el foco al botón "Ejecutar".
  const modalResultado = crearModalDialogo({ titulo: 'Resultado de la ejecución' });
  modalResultado.elemento.classList.add('execution-panel-result-modal');
  modalResultado.cuerpo.append(resultados);

  seccion.append(titulo, ayuda, botonEjecutar);
  seccion.append(estado, error, modalResultado.elemento, modalEntrada.elemento);

  /**
   * Traslada `botonEjecutar` a `[data-canvas-toolbar-run]` si ese
   * contenedor ya existe en el documento (ver "TSK-0079" en la cabecera del
   * archivo). No-op segura si no hay destino (el botón se queda donde ya
   * está) o si ya se trasladó (evita un `append` redundante en cada
   * reintento).
   */
  function trasladarBotonEjecutarALaBarraDeHerramientas() {
    if (destruido) return;
    const destino = document.querySelector('[data-canvas-toolbar-run]');
    if (!destino || botonEjecutar.parentElement === destino) return;
    destino.append(botonEjecutar);
  }

  trasladarBotonEjecutarALaBarraDeHerramientas();
  // `globalThis.queueMicrotask` (no el identificador global bare): mismo
  // patrón ya usado en `editor-controller.js#programarReconstruccion`, que
  // esta ficha no puede tocar pero sí puede imitar (ese global no está
  // declarado en `eslint.config.js`, fuera de las rutas permitidas de esta
  // tarea; `no-undef` lo rechazaría como identificador bare).
  globalThis.queueMicrotask(trasladarBotonEjecutarALaBarraDeHerramientas);

  // --------------------------------------------------------------------
  // Manejadores
  // --------------------------------------------------------------------

  function ocultarError() {
    error.hidden = true;
    error.textContent = '';
  }

  function ocultarResultados() {
    if (modalResultado.estaAbierto()) modalResultado.cerrar();
    resultados.hidden = true;
    resultadosCuerpo.replaceChildren();
  }

  /**
   * Etiqueta de la ventana que pide UN dato: nombra la variable que el motor
   * está esperando y, si el alumno declaró un tipo en ese bloque `Entrada`,
   * también el tipo. Sin nombre de variable (respuesta parcial del servidor)
   * cae a la etiqueta genérica del componente compartido.
   * @param {string | null} variable
   * @param {string} [aviso] Error del intento anterior, cuando se vuelve a
   *   pedir el mismo dato porque lo tecleado no encajaba con el tipo.
   * @returns {string}
   */
  function etiquetaPeticion(variable, aviso) {
    if (!variable) return aviso ?? '';
    const tipo = tipoDeclaradoPorVariable.get(variable);
    /** @type {Record<string, string>} */
    const etiquetasTipo = ETIQUETA_TIPO_PRIMITIVO_ENTRADA;
    const conTipo = tipo ? ` (tipo ${etiquetasTipo[tipo] ?? tipo})` : '';
    const peticion = `Escribe el valor de «${variable}»${conTipo} y pulsa «Enviar»:`;
    return aviso ? `${aviso} ${peticion}` : peticion;
  }

  /**
   * Texto que se enseña dentro de la ventana como contexto: lo que el
   * programa ya ha escrito DESPUÉS del último dato leído, que es justo el
   * mensaje con el que pide este (el equivalente al `print` antes de un
   * `input()`). Es la misma pieza que ya usan las otras dos consolas.
   * @param {ResultadoEjecucionDiagrama} resultado
   * @returns {string}
   */
  function contextoDeLaPeticion(resultado) {
    return resultado.salidasTrasUltimaEntrada.map(formatearValorEjecucion).join('\n');
  }

  /**
   * Pide un dato hasta que sea válido para el tipo declarado de la variable
   * (o hasta que se cancele). Devuelve el valor ya interpretado, o `null` si
   * el alumno cancela.
   *
   * Se vuelve a PREGUNTAR en vez de rechazar y parar: es un dato tecleado a
   * mitad de una ejecución, no un formulario que se pueda revisar entero.
   * @param {ResultadoEjecucionDiagrama} resultado
   * @returns {Promise<number|string|boolean|null>}
   */
  async function pedirValorValido(resultado) {
    const variable = resultado.variablePendiente;
    const tipo = variable ? (tipoDeclaradoPorVariable.get(variable) ?? null) : null;
    const contexto = contextoDeLaPeticion(resultado);
    let aviso = undefined;

    for (;;) {
      const crudo = await pedirDato(contexto, etiquetaPeticion(variable, aviso));
      if (crudo === null || destruido) return null;

      const validacion = validarValorEntradaSegunTipo(crudo, tipo);
      if (validacion.valido) return validacion.valor;

      // Se reabre la ventana con el motivo delante de la petición. El
      // componente fija la etiqueta con `setSafeText` al abrir, así que el
      // lector de pantalla lo anuncia como parte del diálogo nuevo.
      aviso = validacion.mensaje;
      estado.textContent = validacion.mensaje;
    }
  }

  function manejarClicEjecutar() {
    void ejecutarConReplay();
  }

  /**
   * Ejecuta el diagrama pidiendo los datos según los va necesitando (ver la
   * cabecera del archivo). Un solo `AbortController` para toda la tanda: una
   * segunda pulsación de "Ejecutar" cancela la anterior ENTERA, no solo su
   * última petición, que es lo que el alumno espera al volver a pulsar.
   */
  async function ejecutarConReplay() {
    if (!esNodo(programa)) {
      error.hidden = false;
      error.textContent = 'No hay ningún diagrama que ejecutar todavía.';
      return;
    }

    ocultarError();
    ocultarResultados();
    botonEjecutar.disabled = true;
    botonEjecutar.setAttribute('aria-busy', 'true');
    estado.textContent = 'Ejecutando diagrama…';

    controladorEnCurso?.abort();
    const controlador = new AbortController();
    controladorEnCurso = controlador;

    /** @type {Array<number|string|boolean>} */
    const entradas = [];

    try {
      const irJson = serializarPrograma(programa);

      for (;;) {
        const resultado = await ejecutarDiagrama({
          // COPIA, no la lista viva: `entradas` sigue creciendo en las
          // vueltas siguientes, y entregar la misma referencia dejaría al
          // servicio con una lista que le cambia por debajo.
          programa: irJson,
          entradas: [...entradas],
          signal: controlador.signal,
        });
        if (destruido || controlador.signal.aborted) return;

        if (!resultado.necesitaEntrada) {
          mostrarResultado(resultado);
          return;
        }

        // Tope defensivo compartido con las otras dos consolas: un bucle que
        // lee sin condición de salida pediría datos sin fin (y aquí, además,
        // una petición al servidor por cada uno).
        if (entradas.length >= LIMITE_ENTRADAS) {
          estado.textContent = '';
          error.hidden = false;
          error.textContent = `La ejecución se ha detenido tras ${LIMITE_ENTRADAS} datos: comprueba que el bucle de tu diagrama termina en algún momento.`;
          return;
        }

        estado.textContent = 'Esperando un dato…';
        const valor = await pedirValorValido(resultado);
        if (destruido || controlador.signal.aborted) return;

        if (valor === null) {
          estado.textContent = '';
          error.hidden = false;
          error.textContent = MENSAJE_ENTRADA_CANCELADA;
          return;
        }

        entradas.push(valor);
        estado.textContent = 'Ejecutando diagrama…';
      }
    } catch (excepcion) {
      if (destruido || controlador.signal.aborted) return;
      estado.textContent = '';
      error.hidden = false;
      error.textContent = mensajeDeErrorEjecucion(excepcion);
    } finally {
      // Solo la invocación CORRIENTE (la última en abortar/reemplazar a las
      // anteriores, ver arriba) debe reactivar el botón: si esta llamada ya
      // fue sustituida por una segunda pulsación de "Ejecutar" mientras
      // seguía en curso, `controladorEnCurso` ya apunta a ESE otro
      // `AbortController`, y dejar el botón activo aquí lo desincronizaría
      // del estado "ejecutando" real de la petición todavía pendiente.
      if (!destruido && controladorEnCurso === controlador) {
        botonEjecutar.disabled = false;
        botonEjecutar.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * Texto compacto "var1 = valor1, var2 = valor2" a partir de las
   * variables finales de la ejecución, para la barra de resumen (mockup:
   * "nota = 7 → resultado = "Aprobado""). `Object.entries` en el mismo
   * orden en que el motor las devuelve; sin variables finales (programa sin
   * ninguna), cae a un texto genérico basado en el número de salidas.
   * @param {ResultadoEjecucionDiagrama} resultado
   * @returns {string}
   */
  function resumenVariablesFinales(resultado) {
    const entradas = Object.entries(resultado.variablesFinales);
    if (entradas.length === 0) {
      return resultado.salidas.length === 0
        ? 'sin salidas'
        : `${resultado.salidas.length} salida(s)`;
    }
    return entradas.map(([nombre, valor]) => `${nombre} = ${formatearValorEjecucion(valor)}`).join(', ');
  }

  /** @param {ResultadoEjecucionDiagrama} resultado */
  function mostrarResultado(resultado) {
    if (!resultado.exito) {
      estado.textContent = '';
      error.hidden = false;
      error.textContent =
        resultado.error ?? 'La ejecución no se ha podido completar, pero el servidor no ha indicado el motivo.';

      resultados.hidden = false;
      resultadosTitulo.hidden = true;
      resultadosCuerpo.replaceChildren();
      resumenEjecucion.dataset.exito = 'false';
      resumenEjecucion.textContent = '● Última ejecución incorrecta';
      modalResultado.abrir(botonEjecutar);
      return;
    }

    resultados.hidden = false;
    resultadosTitulo.hidden = false;
    resultadosCuerpo.replaceChildren();

    if (resultado.salidas.length === 0) {
      const sinSalidas = document.createElement('p');
      sinSalidas.className = 'execution-panel-results-empty';
      sinSalidas.textContent = 'El programa no ha generado ninguna salida (no tiene ninguna sentencia "Salida").';
      resultadosCuerpo.append(sinSalidas);
    } else {
      const lista = document.createElement('ol');
      lista.className = 'execution-panel-results-list';
      resultado.salidas.forEach((salida) => {
        const item = document.createElement('li');
        item.textContent = formatearValorEjecucion(salida);
        lista.append(item);
      });
      resultadosCuerpo.append(lista);
    }

    resumenEjecucion.dataset.exito = 'true';
    resumenEjecucion.textContent = `● Última ejecución correcta — ${resumenVariablesFinales(resultado)}`;

    estado.textContent = resultado.limiteAlcanzado
      ? `Ejecución completada con ${resultado.salidas.length} salida(s): se alcanzó el límite de pasos de ejecución.`
      : `Ejecución completada con ${resultado.salidas.length} salida(s).`;

    modalResultado.abrir(botonEjecutar);
  }

  botonEjecutar.addEventListener('click', manejarClicEjecutar);

  function destruir() {
    destruido = true;
    controladorEnCurso?.abort();
    botonEjecutar.removeEventListener('click', manejarClicEjecutar);
    // Retira los overlays de `document.body` (donde los deja el portal al
    // abrirse); sin esto quedarían huérfanos tras destruir el panel.
    // `modalEntrada.destruir()` además RESUELVE con `null` una petición de
    // dato en vuelo, para que el bucle de `ejecutarConReplay` despierte y
    // salga en vez de quedarse colgado esperando a un diálogo ya retirado.
    modalResultado.destruir();
    modalEntrada.destruir();
  }

  if (opciones.root) {
    opciones.root.append(seccion);
  }

  return { elemento: seccion, destruir };
}
